import { memo, useMemo, useState } from "react";
import MZRTPlotLegendStrip from "@/components/charts/MZRTPlotLegendStrip";
import BrushFilterChip from "@/components/dashboard/BrushFilterChip";
import type { FeatureData, MZRTData } from "@/types/spectrum";
import { calculateRI } from "@/utils/featureMapping";
import { representativeFeatures } from "@/utils/observationGrouping";
import { panelCls } from "@/utils/panelCls";
import { MZRTChartRecharts } from "./MZRTChartRecharts";

/** MZRT 표시 밀도: "standard"(서버 기본 데시메이션) | "detail"(전체 피크, max_points 상향). */
export type MZRTDensity = "standard" | "detail";

interface MZRTPlotProps {
  filteredMzrtData: MZRTData[];
  features: FeatureData[];
  selectedFeature: string | null;
  isMsMissing?: boolean;
  onMZRTClick: (data: MZRTData) => void;
  /** RT 필터 활성 상태 라벨. null이면 칩 미표시. */
  brushActiveLabel?: string | null;
  /** RT 필터 해제 핸들러 */
  onClearBrush?: () => void;
  /** 표시 밀도. 미지정 시 "standard". */
  density?: MZRTDensity;
  /** 밀도 변경 핸들러. 지정 시 헤더에 "표시:" 셀렉트가 렌더된다. */
  onDensityChange?: (density: MZRTDensity) => void;
}

const MZRTPlot = ({
  filteredMzrtData,
  features,
  selectedFeature: _selectedFeature,
  isMsMissing = false,
  onMZRTClick,
  brushActiveLabel,
  onClearBrush,
  density = "standard",
  onDensityChange,
}: MZRTPlotProps) => {
  const [xAxisType, setXAxisType] = useState<"rt" | "ri">("rt");

  // x축 타입에 따른 데이터 변환 (MZRTChartRecharts에 전달)
  const transformedData = useMemo(() => {
    if (xAxisType === "ri") {
      return filteredMzrtData.map((item) => ({
        ...item,
        ri: calculateRI(item.rt),
      }));
    }
    return filteredMzrtData;
  }, [filteredMzrtData, xAxisType]);

  // T1: MZRT 점·범례의 단위는 관찰(스캔). mzrt-data 는 스캔을 관찰의 1순위
  // 후보 F-번호로 라벨하므로 대표 후보만 쓴다 (형제 후보는 점이 없다).
  const allRepresentatives = useMemo(
    () => representativeFeatures(features),
    [features],
  );

  /**
   * **화면에 실제로 점이 있는 관찰만** 남긴다 (2026-09-06).
   *
   * 브러시하면 점은 `filteredMzrtData` 로 걸러지는데 범례·시리즈는 걸러지지
   * 않은 `features` 에서 왔다. 그래서 RT 13.9~20.2 로 좁혔을 때 점은 4개인데
   * 범례는 "F1 F10 F31 … 외 47건" 을 그대로 달고, 아래 문구는 "관찰 59건
   * 전부 표시됩니다" 라고 말했다 — 화면에 없는 관찰을 가리키는 범례이자
   * 사실이 아닌 문구다.
   *
   * 대표 후보 선정 자체는 전체 `features` 로 해야 한다(형제 후보 중 누가
   * 대표인지는 구간과 무관한 사실이다). 고른 뒤에 보이는 것만 남긴다.
   */
  const visibleFeatureIds = useMemo(() => {
    const seen = new Set<string>();
    for (const point of filteredMzrtData) {
      if (point.feature_id) seen.add(point.feature_id);
    }
    return seen;
  }, [filteredMzrtData]);

  const representatives = useMemo(
    () =>
      allRepresentatives.filter((feature) =>
        visibleFeatureIds.has(feature.feature_id),
      ),
    [allRepresentatives, visibleFeatureIds],
  );

  /**
   * 관찰별 최대 intensity — 점 색(스캔 내 상대강도)의 **분모**.
   *
   * 2026-09-06: 모집단을 `representatives` 가 아니라 **점 자체**로 바꿨다.
   * 종전에는 대표 후보 목록으로 키를 만들었는데, 라벨된 점의 feature_id 가
   * 그 목록에 없으면(형제 후보 id 로 라벨된 점 등) 호출부의 `?? 0` 이 걸려
   * 분모가 0 이 됐다. 그러면 `identifiedPointColor` 가 램프 하한을 돌려주므로
   * **"분모를 모른다"와 "상대강도 0%"가 화면에서 같은 색**이 된다 — 증거
   * 화면에서 두 사실이 한 표기를 쓰면 안 된다. 점에서 만들면 라벨된 점은
   * 정의상 자기 분모를 갖는다.
   *
   * 덤으로 O(관찰 × 점) → O(점). 종전에는 관찰 170건 × 점 1,700개 만큼 훑었다.
   */
  const featureMaxIntensities = useMemo(() => {
    const maxIntensities: Record<string, number> = {};
    for (const point of filteredMzrtData) {
      if (!point.feature_id) continue;
      const intensity = point.intensity || 0;
      if (intensity > (maxIntensities[point.feature_id] ?? 0)) {
        maxIntensities[point.feature_id] = intensity;
      }
    }
    return maxIntensities;
  }, [filteredMzrtData]);

  /** 이 분석의 전체 관찰 수 — 문구가 "전부"인지 "이 구간"인지 가르는 기준. */
  const totalObservationCount = allRepresentatives.length;

  /** 화면에 점이 있는 동정 관찰 수. 캡션이 말하는 유일한 완전성 단위다. */
  const shownObservationCount = representatives.length;

  /**
   * 브러시 구간에 관찰이 하나도 없을 때 (2026-09-06).
   *
   * uPlot 은 최소 드래그 거리가 없어 1px 드래그로도 구간이 잡힌다. 그 구간에
   * 관찰이 없으면 종전에는 **빈 격자만** 남았고, 칩은 여전히 "필터링됨"
   * 이라고 말했다. 증거 화면에서 빈 격자는 "이 구간에서 아무것도 검출되지
   * 않았다"로 읽힌다 — 실제로는 "고른 구간에 표시할 관찰이 없다"이다.
   * 두 사실은 다르므로 구분해서 말한다.
   */
  if (!isMsMissing && filteredMzrtData.length === 0 && brushActiveLabel) {
    return (
      <div
        className={panelCls(
          "standard",
          "justify-center gap-4 flex flex-col m-auto h-full",
        )}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center flex-wrap">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            <span>m/z vs {xAxisType === "rt" ? "RT" : "RI"} Plot</span>
            <span className="ml-2 text-sm font-normal text-gray-500">
              (질량/체류시간 분포)
            </span>
          </h3>
          {onClearBrush && (
            <BrushFilterChip
              brushLabel={brushActiveLabel}
              onClear={onClearBrush}
            />
          )}
        </div>
        <p className="text-sm text-gray-600">
          이 구간에 표시할 관찰이 없습니다. 분석 전체로는 관찰{" "}
          {totalObservationCount}건이 있습니다 — 구간을 넓히거나 필터를
          해제하세요.
        </p>
        <p className="text-xs text-gray-500">
          검출되지 않았다는 뜻이 아닙니다. 고른 구간 안에 기록된 관찰이 없다는
          뜻입니다.
        </p>
      </div>
    );
  }

  // MS 원본 결측(TIC-only) 시료: m/z×RT 평면 표시 불가 — 정직 안내.
  // 모든 hook 호출 뒤에 위치해야 함 (조건부 hook 금지).
  if (isMsMissing) {
    return (
      <div
        className={panelCls(
          "standard",
          "justify-center gap-4 flex flex-col m-auto h-full",
        )}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center flex-wrap">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            <span>m/z vs RT Plot</span>
            <span className="ml-2 text-sm font-normal text-gray-500">
              (질량/체류시간 분포)
            </span>
          </h3>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            MS 미수집 시료 — m/z × RT 평면 표시 불가
          </p>
          <p className="mt-2 text-xs text-gray-500">
            원본 장비 데이터에 MS 미수집 (결측이며 비검출 아님)
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={panelCls(
        "standard",
        "justify-center gap-10 flex flex-col m-auto h-full",
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center flex-wrap">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            <span>m/z vs {xAxisType === "rt" ? "RT" : "RI"} Plot</span>
            <span className="ml-2 text-sm font-normal text-gray-500">
              (질량/체류시간 분포)
            </span>
          </h3>
          {brushActiveLabel != null && onClearBrush && (
            <BrushFilterChip
              brushLabel={brushActiveLabel}
              onClear={onClearBrush}
            />
          )}
        </div>
        <div className="flex items-center space-x-4">
          {onDensityChange && (
            <div className="flex items-center space-x-2">
              <label
                htmlFor="mzrtDensitySelect"
                className="text-sm text-gray-600"
              >
                표시:
              </label>
              <select
                id="mzrtDensitySelect"
                value={density}
                onChange={(e) => onDensityChange(e.target.value as MZRTDensity)}
                className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
              >
                <option value="standard">표준</option>
                <option value="detail">상세(전체 피크)</option>
              </select>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <label htmlFor="xAxisSelect" className="text-sm text-gray-600">
              X축:
            </label>
            <select
              id="xAxisSelect"
              value={xAxisType}
              onChange={(e) => setXAxisType(e.target.value as "rt" | "ri")}
              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
            >
              <option value="rt">RT (min)</option>
              <option value="ri">RI</option>
            </select>
          </div>
        </div>
      </div>

      {/* 차트와 캡션을 한 자식으로 묶는다 — 루트 flex 의 `gap-10`(40px)이
          캡션 때문에 한 번 더 벌어지지 않게. 캡션은 마우스 기하 측정 래퍼
          **밖**의 형제라, 캡션 위 호버가 RT 커서선을 움직이지 않는다(종전
          범례는 그 래퍼 안에 포털로 들어가 있어 실제로 움직였다). */}
      <div className="cursor-pointer">
        <MZRTChartRecharts
          data={transformedData}
          featureMaxIntensities={featureMaxIntensities}
          xAxisType={xAxisType}
          onClick={onMZRTClick}
          features={features}
        />
      </div>

      {/* 범례 한 줄 — 색 키·관찰 수·강도 램프·단서(ⓘ)를 한 줄에 담는다.
          차트 밖 형제 DOM 이라 이 위 호버·클릭이 RT 커서선·선택을 건드리지
          않고(종전 도트 범례는 측정 래퍼 안 포털이라 실제로 건드렸다),
          Phase E 에서 차트를 갈아도 남는다.

          P2-3 / B-4: store slice 직접 구독 — ScatterChart 는 리렌더되지 않는다.

          2026-09-06: 여기 있던 별도 문단 둘("동정된 관찰 N건이 모두 화면에…",
          "이 구간에는 동정된 관찰이 없습니다…")을 이 한 줄에 합쳤다. 색 키의
          라벨이 곧 개수이므로 같은 사실을 두 번 쓰지 않는다 — 운영자: "하단
          스펙트럼 주위의 쓸데없는 말이 많아. 핵심만 간결하게." */}
      <MZRTPlotLegendStrip
        features={features}
        shownObservationCount={shownObservationCount}
        totalObservationCount={totalObservationCount}
        visibleFeatureIds={visibleFeatureIds}
      />
    </div>
  );
};

export default memo(MZRTPlot);
